import React, { useEffect, useState } from 'react';
import StructuredMessage from './StructuredMessage';
import { TypingFact } from './WaitingEntertainment';

// Local preview data exercises the real chat rendering without a login or API call.
// Most photos are generic sample assets; the MISSHA example uses public catalog
// swatch assets so we can inspect the inaccurate-hex regression directly.
const samplePhotoA = 'https://images.unsplash.com/photo-1620916566398-39f1143ab7be?auto=format&fit=crop&w=600&q=80';
const samplePhotoB = 'https://images.unsplash.com/photo-1631730359585-38a4935cbec4?auto=format&fit=crop&w=600&q=80';

const examples = {
  amuse: {
    label: 'Corrected AMUSE shade',
    content: {
      advice: 'For AMUSE Dew Jelly Master Cushion, 01 Clean is the closest match.',
      products: '**AMUSE Dew Jelly Master Cushion by AMUSE**\nRecommended shade: 01 Clean',
    },
    shadeMatches: [{
      product_title: 'AMUSE Dew Jelly Master Cushion', shade_name: '01 Clean',
      hex_color: '#e4d1d5',
      swatch_image_url: 'https://doatsodmpimjcqtpdawx.supabase.co/storage/v1/object/public/catalog-images/heroes/d2bf0fe1-7c30-4091-883b-1e9f8a6494f2/01_Clean_hero.jpg',
    }],
  },
  missha: {
    label: 'Catalog swatch images',
    content: {
      advice: 'For MISSHA M Perfect Cover Serum B.B. Cream, #21 Light Beige is the closest match, with #23 Neutral Beige as an alternative.',
      products: '**MISSHA M Perfect Cover Serum B.B. Cream by MISSHA**\nRecommended shade: #21 Light Beige\nAlternative shade: #23 Neutral Beige',
    },
    shadeMatches: [
      { product_title: 'MISSHA M Perfect Cover Serum B.B. Cream', shade_name: '#21 Light Beige', hex_color: '#b396c8', swatch_image_url: 'https://doatsodmpimjcqtpdawx.supabase.co/storage/v1/object/public/catalog-images/swatches/5b51f9aa-2f5e-47f5-8311-a47b00c3476a/_21_Light_Beige_swatch.jpg', product_hero_image_url: 'https://doatsodmpimjcqtpdawx.supabase.co/storage/v1/object/public/catalog-images/swatches/5b51f9aa-2f5e-47f5-8311-a47b00c3476a/_21_Light_Beige_swatch.jpg' },
      { product_title: 'MISSHA M Perfect Cover Serum B.B. Cream', shade_name: '#23 Neutral Beige', hex_color: '#b395c8', swatch_image_url: 'https://doatsodmpimjcqtpdawx.supabase.co/storage/v1/object/public/catalog-images/swatches/5b51f9aa-2f5e-47f5-8311-a47b00c3476a/_23_Neutral_Beige_swatch.jpg', product_hero_image_url: 'https://doatsodmpimjcqtpdawx.supabase.co/storage/v1/object/public/catalog-images/swatches/5b51f9aa-2f5e-47f5-8311-a47b00c3476a/_21_Light_Beige_swatch.jpg' },
    ],
  },
  foundations: {
    label: 'One + two shades',
    content: {
      advice: '01 Porcelain is the closest match in Fixing Dew Cushion. For HERA, 17N1 is the closest match, with 13N1 as a brighter option.',
      products: '**too cool for school Fixing Dew Cushion by TOO COOL FOR SCHOOL**\nRecommended shade: 01 Porcelain\n\n**Reflection Skin Glow Cushion Foundation by HERA**\nRecommended shade: 17N1\nAlternative shade: 13N1',
    },
    shadeMatches: [
      { product_title: 'too cool for school Fixing Dew Cushion', shade_name: '01 Porcelain', hex_color: '#f4ddce', product_hero_image_url: samplePhotoA },
      { product_title: 'Reflection Skin Glow Cushion Foundation', shade_name: '17N1', hex_color: '#e5c9b8', product_hero_image_url: samplePhotoB },
      { product_title: 'Reflection Skin Glow Cushion Foundation', shade_name: '13N1', hex_color: '#f1d9ca', product_hero_image_url: samplePhotoB },
    ],
  },
  long: {
    label: 'Long shade names',
    content: {
      advice: 'Creme Brulee is the best match, with Madeleine as a more neutral option.',
      products: '**Radiant Creamy Concealer with Hydrating Medium Coverage by NARS**\nRecommended shade: Creme Brulee – L2.5 – light with cool undertones',
    },
    shadeMatches: [
      { product_title: 'Radiant Creamy Concealer with Hydrating Medium Coverage', shade_name: 'Madeleine – L2.3 – light with neutral undertones', hex_color: '#f1cbb2', product_hero_image_url: samplePhotoA },
      { product_title: 'Radiant Creamy Concealer with Hydrating Medium Coverage', shade_name: 'Creme Brulee – L2.5 – light with cool undertones', hex_color: '#e4bcb2', product_hero_image_url: samplePhotoA },
      { product_title: 'Radiant Creamy Concealer with Hydrating Medium Coverage', shade_name: 'Cannelle – L2.7 – light with warm undertones', hex_color: '#dda995', product_hero_image_url: samplePhotoA },
    ],
  },
  short: {
    label: 'Three short names',
    content: {
      advice: '#N Light is the closest match for your skin.',
      products: '**Skin Nuder Cover Layer Cushion by JUNGSAEMMOOL**\nRecommended shade: #N Light',
    },
    shadeMatches: [
      { product_title: 'Skin Nuder Cover Layer Cushion', shade_name: '#Fair Light', hex_color: '#f5e2cd', product_hero_image_url: samplePhotoB },
      { product_title: 'Skin Nuder Cover Layer Cushion', shade_name: '#N Light', hex_color: '#eed6bc', product_hero_image_url: samplePhotoB },
      { product_title: 'Skin Nuder Cover Layer Cushion', shade_name: '#Light', hex_color: '#eed0ad', product_hero_image_url: samplePhotoB },
    ],
  },
  products: {
    label: 'Product images',
    content: {
      advice: 'Both are hydrating serum steps with different strengths.',
      products: '**The 6 Peptide Skin Booster Serum by COSRX**\n\n**Derma Healer Pore Tightening Ampoule by REJURAN**',
    },
    shadeMatches: [
      { product_title: 'The 6 Peptide Skin Booster Serum', product_hero_image_url: samplePhotoA },
      { product_title: 'Derma Healer Pore Tightening Ampoule', product_hero_image_url: samplePhotoB },
    ],
  },
  choices: {
    label: 'Raised choices',
    content: {
      advice: 'Pick the exact concealer range and I’ll match your shade.',
      clarify: {
        question: 'Which NARS concealer do you mean?',
        options: [
          'NARS Radiant Creamy Concealer with Hydrating Medium Coverage',
          'NARS Mini Radiant Creamy Concealer with Medium Coverage',
          'NARS Soft Matte Complete Full Coverage Longwear Concealer with Hyaluronic Acid',
          'NARS Light Reflecting™ Undereye Brightener',
        ],
      },
    },
    shadeMatches: [],
  },
  productChoices: {
    label: 'Product choices with photos',
    content: {
      advice: 'Choose the MISSHA BB cream line you want matched.',
      clarify: {
        question: 'Which MISSHA BB cream do you mean?',
        options: [
          'MISSHA M Perfect Cover Serum B.B. Cream',
          'MISSHA M Perfect Cover BB Cream EX SPF42/PA++',
          'MISSHA M Choboyang BB Cream',
        ],
        product_images: {
          'MISSHA M Perfect Cover Serum B.B. Cream': 'https://doatsodmpimjcqtpdawx.supabase.co/storage/v1/object/public/catalog-images/swatches/5b51f9aa-2f5e-47f5-8311-a47b00c3476a/_21_Light_Beige_swatch.jpg',
          'MISSHA M Perfect Cover BB Cream EX SPF42/PA++': 'https://doatsodmpimjcqtpdawx.supabase.co/storage/v1/object/public/catalog-images/swatches/75c5458c-3353-4abc-ad9f-c3418842d2ed/_21_swatch.jpg',
          'MISSHA M Choboyang BB Cream': 'https://doatsodmpimjcqtpdawx.supabase.co/storage/v1/object/public/catalog-images/swatches/37569ae0-0c51-48c7-bb9b-87eadda1c03d/No._21_Illuminating_Beige_swatch.jpg',
        },
      },
    },
    shadeMatches: [],
  },
  facts: {
    label: 'Skincare facts',
  },
  messages: {
    label: 'User messages',
  },
};

export default function ProductUIReviewPreview() {
  const [active, setActive] = useState('foundations');
  const [answer, setAnswer] = useState('');
  const example = examples[active];
  const simulateAndroid = new URLSearchParams(window.location.search).get('android-ui') === '1';

  useEffect(() => {
    if (!simulateAndroid) return undefined;
    const root = document.documentElement;
    const alreadyAndroid = root.classList.contains('android-web');
    root.classList.add('android-web');
    return () => { if (!alreadyAndroid) root.classList.remove('android-web'); };
  }, [simulateAndroid]);

  return (
    <div className="h-[100dvh] overflow-y-auto bg-white" style={{ fontFamily: "'DM Sans', Arial, sans-serif" }}>
      <div className="bg-[#341C1C] text-white text-center py-4 text-[16px] font-semibold">✦ Son:mi</div>
      <div className="bg-[#F1EEE9] px-4 py-3 text-center text-[12px] text-[#2A324B]">
        Layout preview{simulateAndroid ? ' · Android layout simulation' : ''} · sample and catalog photos · no account or API needed
      </div>
      <div className="max-w-[430px] mx-auto px-4 pb-12">
        <nav className="flex flex-wrap gap-2 py-4" aria-label="Preview states">
          {Object.entries(examples).map(([key, value]) => (
            <button
              key={key}
              type="button"
              onClick={() => { setActive(key); setAnswer(''); }}
              aria-pressed={active === key}
              className={`rounded-full px-3 py-2 text-[12px] border ${active === key ? 'bg-[#2A324B] text-white border-[#2A324B]' : 'bg-white text-[#2A324B] border-[#2A324B]/20'}`}
            >
              {value.label}
            </button>
          ))}
        </nav>
        {active === 'messages' ? (
          <div className="pt-3 space-y-4">
            <p className="text-[12px] text-[#2A324B]/60">Short and long messages use the same outgoing style.</p>
            <div className="chat-bubble chat-bubble--user">What is my shade in NARS concealer?</div>
            <div className="chat-bubble chat-bubble--user">I need to buy a dewy cushion that’s shade matched to my skin.</div>
            <div className="chat-bubble chat-bubble--user">NARS Radiant Creamy Concealer with Hydrating Medium Coverage</div>
          </div>
        ) : active === 'facts' ? (
          <div className="pt-4 text-[15px] text-[#2A324B]">
            <p>Still working…</p>
            <TypingFact isLoading />
          </div>
        ) : (
          <>
            <div className="border-b border-[#2A324B]/10 pb-2 mb-4 text-[13px] font-medium text-[#2A1212]">Advice</div>
            <StructuredMessage
              key={active}
              content={example.content}
              shadeMatches={example.shadeMatches}
              onAsk={setAnswer}
              userId="preview-user"
              onSaveProduct={async () => true}
            />
          </>
        )}
        {answer && <div className="chat-bubble chat-bubble--user mt-5" aria-live="polite">{answer}</div>}
      </div>
    </div>
  );
}
